import { Button, Card, Icon, SegmentedControl } from "@helmr/ui";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { LeadFilters } from "../../api/leads";
import { NoAccess } from "../../app/NoAccess";
import { areaAllows } from "../../app/nav";
import { Board } from "./Board";
import { LeadList } from "./LeadList";
import { useScope, useStaffNames, type Scope } from "./hooks";
import { leads as t } from "./strings";
import s from "./leads.module.css";

type View = "list" | "board";

/** `own`-scoped roles only ever see their own leads, so an owner filter would filter nothing. */
const canFilterOwner = (sc: Scope) => {
  const g = sc.ctx.me?.grants;
  return !g || "owner" in g || g.scope.clients === "all";
};

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => { const id = setTimeout(() => setV(value), ms); return () => clearTimeout(id); }, [value, ms]);
  return v;
}

export function LeadsPage() {
  const sc = useScope();
  const staff = useStaffNames(sc);
  const [params, setParams] = useSearchParams();
  const view: View = params.get("view") === "board" ? "board" : "list";
  const stage = params.get("stage") ?? "";
  const owner = params.get("owner") ?? "";
  const source = params.get("source") ?? "";
  const [search, setSearch] = useState(params.get("q") ?? "");
  const q = useDebounced(search.trim(), 250);

  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) { if (v) next.set(k, v); else next.delete(k); }
    setParams(next, { replace: true });
  };
  useEffect(() => { if ((params.get("q") ?? "") !== q) set({ q: q || null }); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const filters: LeadFilters = useMemo(() => ({
    ...(q ? { q } : {}),
    ...(owner === "me" ? { owner: sc.ctx.me?.staff.id } : owner ? { owner } : {}),
    ...(source ? { source } : {}),
    ...(view === "list" && stage ? { stage } : {}),
  }), [q, owner, source, stage, view, sc.ctx.me?.staff.id]);
  const hasFilters = !!(q || owner || source || stage);

  if (sc.ctx.status !== "loading" && !areaAllows("leads", sc.ctx.has)) return <NoAccess />;

  return (
    <div className={s.page}>
      <title>Leads · Helmr</title>
      <div className={s.head}>
        <div>
          <h1 tabIndex={-1}>{t.title}</h1>
          <p>{t.sub}</p>
        </div>
        <div className={s.headRight}>
          <SegmentedControl label={t.views.label} value={view} onChange={(v) => set({ view: v === "board" ? "board" : null })} options={[{ value: "list", label: t.views.list }, { value: "board", label: t.views.board }]} />
          {sc.ctx.has("leads.edit") ? <Button as={Link} to="/leads/trial">{t.bookTrial}</Button> : null}
          {sc.ctx.has("leads.add") ? <Button as={Link} to="/leads/new" variant="primary"><Icon name="plus" />{t.add}</Button> : null}
        </div>
      </div>

      <div className={s.filters} role="search" aria-label={t.filters.label}>
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t.filters.search} aria-label={t.filters.search} maxLength={60} />
        {canFilterOwner(sc) ? (
          <select value={owner} onChange={(e) => set({ owner: e.target.value || null })} aria-label={t.filters.owner}>
            <option value="">{t.filters.owner}: {t.filters.anyone}</option>
            <option value="me">{t.filters.mine}</option>
            <option value="none">{t.filters.unassigned}</option>
            {staff.list.filter((p) => p.id !== sc.ctx.me?.staff.id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        ) : null}
        <select value={source} onChange={(e) => set({ source: e.target.value || null })} aria-label={t.filters.source}>
          <option value="">{t.filters.allSources}</option>
          {Object.entries(t.sources).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
      </div>

      {view === "board" ? <Board sc={sc} filters={filters} hasFilters={hasFilters} /> : (
        <Card>
          <LeadList sc={sc} filters={filters} stage={stage} onStage={(v) => set({ stage: v || null })} hasFilters={hasFilters} />
        </Card>
      )}
    </div>
  );
}
